// config/themes.js
// 6 ערכות נושא — 3 תמונתיים (gradient fallback) + 3 נקיים
// Theme נשמר ב-localStorage key: 'madad_theme'
// TODO: migrate to Backend API when available

import { createTheme } from '@mui/material/styles';

// =============================================================================
// פונטים משותפים
// =============================================================================
const FONT_FAMILY_BODY = "'Heebo', sans-serif";
const FONT_FAMILY_HEADING = "'Rubik', sans-serif";
const FONT_FAMILY_MONO = "'IBM Plex Mono', monospace";

const sharedTypography = {
  fontFamily: FONT_FAMILY_BODY,
  h1: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.2 },
  h2: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.25 },
  h3: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.3 },
  h4: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.35 },
  h5: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 500, fontSize: '1.125rem', lineHeight: 1.4 },
  h6: { fontFamily: FONT_FAMILY_HEADING, fontWeight: 500, fontSize: '1rem', lineHeight: 1.4 },
  body1: { fontFamily: FONT_FAMILY_BODY, fontWeight: 400, fontSize: '0.9375rem', lineHeight: 1.6 },
  body2: { fontFamily: FONT_FAMILY_BODY, fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.5 },
  subtitle1: { fontFamily: FONT_FAMILY_BODY, fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 },
  subtitle2: { fontFamily: FONT_FAMILY_BODY, fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
  button: { fontFamily: FONT_FAMILY_BODY, fontWeight: 500, fontSize: '0.875rem', textTransform: 'none' },
  caption: { fontFamily: FONT_FAMILY_BODY, fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.5 },
  overline: { fontFamily: FONT_FAMILY_BODY, fontWeight: 500, fontSize: '0.6875rem', letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: FONT_FAMILY_MONO, fontWeight: 400, fontSize: '0.875rem' },
};

// =============================================================================
// הגדרות RTL
// =============================================================================
const sharedDirection = 'rtl';

// =============================================================================
// Component overrides משותפים
// =============================================================================
const sharedComponents = (mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        direction: 'rtl',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-track': { background: mode === 'dark' ? '#1a1a2e' : '#f0f0f0' },
        '&::-webkit-scrollbar-thumb': {
          background: mode === 'dark' ? '#4a4a6a' : '#c0c0c0',
          borderRadius: 4,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        padding: '8px 20px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
      },
      contained: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          transform: 'translateY(-1px)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 10,
          transition: 'box-shadow 0.2s ease',
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${mode === 'dark' ? 'rgba(100, 180, 255, 0.15)' : 'rgba(21, 101, 192, 0.12)'}`,
          },
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: FONT_FAMILY_BODY,
        fontSize: '0.8125rem',
        borderRadius: 8,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        fontWeight: 500,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontFamily: FONT_FAMILY_BODY,
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      },
      head: {
        fontWeight: 600,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: FONT_FAMILY_BODY,
        fontWeight: 500,
        textTransform: 'none',
        fontSize: '0.9375rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 10,
      },
    },
  },
});

// =============================================================================
// Glassmorphism helpers
// =============================================================================
const glassmorphism = {
  dark: {
    card: {
      background: 'rgba(255, 255, 255, 0.06)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    },
    sidebar: {
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      // RTL: sidebar בצד ימין, הגבול בצד שמאל שלו (= inline-start)
      // stylis-plugin-rtl לא הופך borderInlineStart אז זה בטוח
      borderInlineStart: '1px solid rgba(255, 255, 255, 0.08)',
    },
    header: {
      background: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    },
    input: {
      background: 'rgba(255, 255, 255, 0.05)',
    },
  },
  light: {
    card: {
      background: 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.6)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    },
    sidebar: {
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(20px)',
      // RTL: sidebar בצד ימין, הגבול בצד שמאל שלו (= inline-start)
      borderInlineStart: '1px solid rgba(0, 0, 0, 0.06)',
    },
    header: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    },
    input: {
      background: 'rgba(255, 255, 255, 0.9)',
    },
  },
};

// =============================================================================
// Score colors — צבעי ציון משותפים
// =============================================================================
const scoreColors = {
  excellent: '#4caf50',   // 8-10
  good: '#8bc34a',        // 6-8
  average: '#ff9800',     // 4-6
  poor: '#f44336',        // 0-4
};

// =============================================================================
// הגדרות ה-Themes
// =============================================================================
export const themeDefinitions = {
  // =========================================================================
  // Theme 1: עירוני (Urban) — dark, תמונת רקע
  // =========================================================================
  urban: {
    name: 'עירוני',
    nameEn: 'Urban',
    mode: 'dark',
    hasBackgroundImage: true,
    // Unsplash CDN — city skyline (verify URL if image doesn't load; gradient fallback active)
    backgroundImage: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1920&q=80',
    overlayOpacity: 0.7,
    backgroundFallback: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    overlayColor: 'rgba(10, 10, 30, 0.7)',
    palette: {
      mode: 'dark',
      primary: { main: '#00b0ff', light: '#69e2ff', dark: '#0081cb', contrastText: '#000' },
      secondary: { main: '#ff6e40', light: '#ffa06d', dark: '#c53d13', contrastText: '#000' },
      background: { default: '#0a0a1e', paper: '#141432' },
      text: { primary: '#e8eaf6', secondary: '#9fa8da' },
      error: { main: '#ff5252' },
      warning: { main: '#ffc107' },
      success: { main: '#69f0ae' },
      info: { main: '#40c4ff' },
      divider: 'rgba(255, 255, 255, 0.08)',
    },
    glassmorphism: glassmorphism.dark,
    scoreColors,
    sidebar: {
      width: 260,
      background: 'linear-gradient(180deg, rgba(15, 12, 41, 0.95) 0%, rgba(48, 43, 99, 0.9) 100%)',
      activeItemBg: 'rgba(0, 176, 255, 0.15)',
      activeItemBorder: '#00b0ff',
      hoverBg: 'rgba(0, 176, 255, 0.08)',
      textColor: '#c5cae9',
      activeTextColor: '#00b0ff',
      iconColor: '#7986cb',
      activeIconColor: '#00b0ff',
    },
    login: {
      // Urban skyline background
      background: 'url(https://images.unsplash.com/photo-1514565131-fce0801e5785?w=1920&q=80) center/cover no-repeat, linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      formBg: 'rgba(20, 20, 50, 0.85)',
      formBorder: 'rgba(0, 176, 255, 0.2)',
      formShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
    },
  },

  // =========================================================================
  // Theme 2: טבע (Nature) — light, תמונת רקע
  // =========================================================================
  nature: {
    name: 'טבע',
    nameEn: 'Nature',
    mode: 'light',
    hasBackgroundImage: true,
    // Unsplash CDN — green forest path (verified URL)
    backgroundImage: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80',
    overlayOpacity: 0.6,
    backgroundFallback: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 30%, #81c784 60%, #e8f5e9 100%)',
    overlayColor: 'rgba(255, 255, 255, 0.6)',
    palette: {
      mode: 'light',
      primary: { main: '#2e7d32', light: '#60ad5e', dark: '#005005', contrastText: '#fff' },
      secondary: { main: '#795548', light: '#a98274', dark: '#4b2c20', contrastText: '#fff' },
      background: { default: '#f1f8e9', paper: '#ffffff' },
      text: { primary: '#1b5e20', secondary: '#4e6e50' },
      error: { main: '#c62828' },
      warning: { main: '#e65100' },
      success: { main: '#2e7d32' },
      info: { main: '#0277bd' },
      divider: 'rgba(0, 0, 0, 0.08)',
    },
    glassmorphism: glassmorphism.light,
    scoreColors,
    sidebar: {
      width: 260,
      background: 'linear-gradient(180deg, rgba(200, 230, 201, 0.95) 0%, rgba(165, 214, 167, 0.9) 100%)',
      activeItemBg: 'rgba(46, 125, 50, 0.12)',
      activeItemBorder: '#2e7d32',
      hoverBg: 'rgba(46, 125, 50, 0.06)',
      textColor: '#33691e',
      activeTextColor: '#2e7d32',
      iconColor: '#558b2f',
      activeIconColor: '#2e7d32',
    },
    login: {
      // Green forest background
      background: 'url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80) center/cover no-repeat, linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 30%, #81c784 60%, #e8f5e9 100%)',
      formBg: 'rgba(255, 255, 255, 0.88)',
      formBorder: 'rgba(46, 125, 50, 0.2)',
      formShadow: '0 24px 64px rgba(0, 0, 0, 0.12)',
    },
  },

  // =========================================================================
  // Theme 3: דרמטי (Dramatic) — dark, תמונת רקע
  // =========================================================================
  dramatic: {
    name: 'דרמטי',
    nameEn: 'Dramatic',
    mode: 'dark',
    hasBackgroundImage: true,
    // Unsplash CDN — dramatic mountain landscape (verified URL)
    backgroundImage: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&q=80',
    overlayOpacity: 0.8,
    backgroundFallback: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #1a1a2e 100%)',
    overlayColor: 'rgba(10, 10, 20, 0.8)',
    palette: {
      mode: 'dark',
      primary: { main: '#e91e63', light: '#ff6090', dark: '#b0003a', contrastText: '#fff' },
      secondary: { main: '#ffd54f', light: '#ffff81', dark: '#c8a415', contrastText: '#000' },
      background: { default: '#0d0d1a', paper: '#1a1a2e' },
      text: { primary: '#eceff1', secondary: '#90a4ae' },
      error: { main: '#ff1744' },
      warning: { main: '#ff9100' },
      success: { main: '#00e676' },
      info: { main: '#18ffff' },
      divider: 'rgba(255, 255, 255, 0.06)',
    },
    glassmorphism: glassmorphism.dark,
    scoreColors,
    sidebar: {
      width: 260,
      background: 'linear-gradient(180deg, rgba(26, 26, 46, 0.95) 0%, rgba(15, 52, 96, 0.9) 100%)',
      activeItemBg: 'rgba(233, 30, 99, 0.15)',
      activeItemBorder: '#e91e63',
      hoverBg: 'rgba(233, 30, 99, 0.08)',
      textColor: '#b0bec5',
      activeTextColor: '#e91e63',
      iconColor: '#78909c',
      activeIconColor: '#e91e63',
    },
    login: {
      // Dramatic mountain landscape background (verified URL)
      background: 'url(https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&q=80) center/cover no-repeat, linear-gradient(135deg, #1a1a2e 0%, #16213e 40%, #0f3460 70%, #1a1a2e 100%)',
      formBg: 'rgba(26, 26, 46, 0.88)',
      formBorder: 'rgba(233, 30, 99, 0.2)',
      formShadow: '0 24px 64px rgba(0, 0, 0, 0.6)',
    },
  },

  // =========================================================================
  // Theme 4: קלאסי (Classic) — light, ברירת מחדל
  // =========================================================================
  classic: {
    name: 'קלאסי',
    nameEn: 'Classic',
    mode: 'light',
    isDefault: true,
    hasBackgroundImage: false,
    backgroundPattern: 'radial-gradient(circle at 1px 1px, rgba(21, 101, 192, 0.03) 1px, transparent 0)',
    backgroundPatternSize: '32px 32px',
    backgroundGradient: 'linear-gradient(160deg, #e3f2fd 0%, #f5f5f5 40%, #e8eaf6 100%)',
    palette: {
      mode: 'light',
      primary: { main: '#1565c0', light: '#5e92f3', dark: '#003c8f', contrastText: '#fff' },
      secondary: { main: '#e91e63', light: '#ff6090', dark: '#b0003a', contrastText: '#fff' },
      background: { default: '#f5f7fa', paper: '#ffffff' },
      text: { primary: '#263238', secondary: '#546e7a' },
      error: { main: '#d32f2f' },
      warning: { main: '#ed6c02' },
      success: { main: '#2e7d32' },
      info: { main: '#0288d1' },
      divider: 'rgba(0, 0, 0, 0.08)',
    },
    glassmorphism: glassmorphism.light,
    scoreColors,
    sidebar: {
      width: 260,
      background: 'linear-gradient(180deg, #1565c0 0%, #0d47a1 100%)',
      activeItemBg: 'rgba(255, 255, 255, 0.18)',
      activeItemBorder: '#ffffff',
      hoverBg: 'rgba(255, 255, 255, 0.1)',
      textColor: 'rgba(255, 255, 255, 0.85)',
      activeTextColor: '#ffffff',
      iconColor: 'rgba(255, 255, 255, 0.7)',
      activeIconColor: '#ffffff',
    },
    login: {
      background: 'linear-gradient(160deg, #e3f2fd 0%, #bbdefb 40%, #e8eaf6 100%)',
      formBg: 'rgba(255, 255, 255, 0.92)',
      formBorder: 'rgba(21, 101, 192, 0.15)',
      formShadow: '0 24px 64px rgba(21, 101, 192, 0.12)',
    },
  },

  // =========================================================================
  // Theme 5: מינימליסטי (Minimal) — light
  // =========================================================================
  minimal: {
    name: 'מינימליסטי',
    nameEn: 'Minimal',
    mode: 'light',
    hasBackgroundImage: false,
    backgroundPattern: 'none',
    backgroundGradient: '#fafafa',
    palette: {
      mode: 'light',
      primary: { main: '#37474f', light: '#62727b', dark: '#102027', contrastText: '#fff' },
      secondary: { main: '#00897b', light: '#4ebaaa', dark: '#005b4f', contrastText: '#fff' },
      background: { default: '#fafafa', paper: '#ffffff' },
      text: { primary: '#212121', secondary: '#757575' },
      error: { main: '#c62828' },
      warning: { main: '#e65100' },
      success: { main: '#2e7d32' },
      info: { main: '#01579b' },
      divider: 'rgba(0, 0, 0, 0.06)',
    },
    glassmorphism: {
      card: {
        background: '#ffffff',
        backdropFilter: 'none',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
      },
      sidebar: {
        background: '#ffffff',
        backdropFilter: 'none',
        // RTL: sidebar בצד ימין, הגבול בצד שמאל שלו (= inline-start)
        borderInlineStart: '1px solid rgba(0, 0, 0, 0.06)',
      },
      header: {
        background: '#ffffff',
        backdropFilter: 'none',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      },
      input: {
        background: '#ffffff',
      },
    },
    scoreColors,
    sidebar: {
      width: 260,
      background: '#ffffff',
      activeItemBg: 'rgba(55, 71, 79, 0.08)',
      activeItemBorder: '#37474f',
      hoverBg: 'rgba(55, 71, 79, 0.04)',
      textColor: '#616161',
      activeTextColor: '#37474f',
      iconColor: '#9e9e9e',
      activeIconColor: '#37474f',
    },
    login: {
      background: '#fafafa',
      formBg: '#ffffff',
      formBorder: 'rgba(0, 0, 0, 0.08)',
      formShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
    },
  },

  // =========================================================================
  // Theme 6: לילה (Night) — dark
  // =========================================================================
  night: {
    name: 'לילה',
    nameEn: 'Night',
    mode: 'dark',
    hasBackgroundImage: false,
    backgroundPattern: 'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.03) 1px, transparent 0)',
    backgroundPatternSize: '32px 32px',
    backgroundGradient: 'linear-gradient(160deg, #121212 0%, #1a1a2e 50%, #16213e 100%)',
    palette: {
      mode: 'dark',
      primary: { main: '#82b1ff', light: '#b6e3ff', dark: '#4d82cb', contrastText: '#000' },
      secondary: { main: '#b388ff', light: '#e7b9ff', dark: '#805acb', contrastText: '#000' },
      background: { default: '#121212', paper: '#1e1e1e' },
      text: { primary: '#e0e0e0', secondary: '#9e9e9e' },
      error: { main: '#ef5350' },
      warning: { main: '#ffa726' },
      success: { main: '#66bb6a' },
      info: { main: '#29b6f6' },
      divider: 'rgba(255, 255, 255, 0.06)',
    },
    glassmorphism: glassmorphism.dark,
    scoreColors,
    sidebar: {
      width: 260,
      background: 'linear-gradient(180deg, #1e1e1e 0%, #252540 100%)',
      activeItemBg: 'rgba(130, 177, 255, 0.12)',
      activeItemBorder: '#82b1ff',
      hoverBg: 'rgba(130, 177, 255, 0.06)',
      textColor: '#b0b0b0',
      activeTextColor: '#82b1ff',
      iconColor: '#787878',
      activeIconColor: '#82b1ff',
    },
    login: {
      background: 'linear-gradient(160deg, #121212 0%, #1a1a2e 50%, #16213e 100%)',
      formBg: 'rgba(30, 30, 30, 0.92)',
      formBorder: 'rgba(130, 177, 255, 0.15)',
      formShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
    },
  },
};

// =============================================================================
// Theme ברירת מחדל
// =============================================================================
export const DEFAULT_THEME_KEY = 'classic';
export const THEME_STORAGE_KEY = 'madad_theme';

// =============================================================================
// רשימת themes לתצוגת בחירה
// =============================================================================
export const themeList = Object.entries(themeDefinitions).map(([key, def]) => ({
  key,
  name: def.name,
  nameEn: def.nameEn,
  mode: def.mode,
  hasBackgroundImage: def.hasBackgroundImage,
  isDefault: def.isDefault || false,
  previewColors: {
    primary: def.palette.primary.main,
    secondary: def.palette.secondary.main,
    background: def.palette.background.default,
    text: def.palette.text.primary,
  },
}));

// =============================================================================
// יצירת MUI theme מתוך theme definition
// =============================================================================
export function buildMuiTheme(themeKey) {
  const def = themeDefinitions[themeKey] || themeDefinitions[DEFAULT_THEME_KEY];

  return createTheme({
    direction: sharedDirection,
    palette: def.palette,
    typography: sharedTypography,
    shape: { borderRadius: 10 },
    components: sharedComponents(def.mode),
    // Custom properties — MUI 5 createTheme מעביר properties לא מוכרים ל-theme object.
    // נגיש דרך theme.custom בכל useTheme() / styled() / sx.
    custom: {
      glassmorphism: def.glassmorphism,
      scoreColors: def.scoreColors,
      sidebar: def.sidebar,
      login: def.login,
      fontMono: FONT_FAMILY_MONO,
      hasBackgroundImage: def.hasBackgroundImage,
      backgroundImage: def.backgroundImage,
      backgroundFallback: def.backgroundFallback,
      backgroundGradient: def.backgroundGradient,
      backgroundPattern: def.backgroundPattern,
      backgroundPatternSize: def.backgroundPatternSize,
      overlayColor: def.overlayColor,
      overlayOpacity: def.overlayOpacity,
    },
  });
}

// =============================================================================
// Helper: קבלת צבע ציון
// =============================================================================
export function getScoreColor(score) {
  if (score == null) return '#9e9e9e';
  if (score >= 8) return scoreColors.excellent;
  if (score >= 6) return scoreColors.good;
  if (score >= 4) return scoreColors.average;
  return scoreColors.poor;
}

// =============================================================================
// Helper: קבלת label ציון — מחזיר i18n key
// שימוש: t(getScoreLabel(score)) או getScoreLabelText(score, t)
// =============================================================================
export function getScoreLabel(score) {
  if (score == null) return 'common.noData';
  if (score >= 8) return 'scoreLabels.excellent';
  if (score >= 6) return 'scoreLabels.good';
  if (score >= 4) return 'scoreLabels.average';
  return 'scoreLabels.poor';
}

// Helper נוח: מחזיר טקסט מתורגם ישירות
export function getScoreLabelText(score, t) {
  return t(getScoreLabel(score));
}
